import type { UiAddressConfig, UiConfig } from '@/config/uiConfig'
import { getShipToAddress } from '@/config/uiConfig'
import type ProviderAPIClient from '@/features/auth/ProviderAPIClient'
import * as Sentry from '@sentry/react'

/**
 * Client-side handler for the ``generateShippingLabel`` order-success action.
 *
 * Contract: ``POST /kits/shipping-label`` (see specs/CONTRACT.md §2). The client
 * sends only ``addressFrom`` / ``addressTo`` — carrier (FedEx) and parcel (LSMC
 * Kit template) are server-side constants and are never sent from here.
 *
 * PII: addresses are never logged. Sentry breadcrumbs note the operation only.
 */

const JSON_HEADERS = { 'Content-Type': 'application/json' }
const ENDPOINT = '/kits/shipping-label'

/**
 * Sub-fields required for a Shippo-usable address, per CONTRACT.md §2 (name,
 * street, city, state, zip are all required — only street2/phone/email/country
 * are optional). ``name`` is enforced here so a nameless address is treated as
 * INCOMPLETE and the client short-circuits to an honest config error instead of
 * POSTing a body the backend would reject with 400/422.
 */
const REQUIRED_ADDRESS_FIELDS = ['name', 'street', 'city', 'state', 'zip'] as const

/** Request body sent to ``POST /kits/shipping-label`` (camelCase, no carrier/parcel). */
export interface ShippingLabelRequest {
  /** Optional; forwarded for audit / idempotency. */
  orderId?: string
  addressFrom: UiAddressConfig
  addressTo: UiAddressConfig
}

/** Successful label purchase (mirrors CONTRACT.md §2 success response). */
export interface ShippingLabelSuccess {
  ok: true
  labelUrl: string
  trackingNumber?: string
  trackingUrlProvider?: string
  carrier?: string
  labelFileType?: string
  transactionId?: string
}

/** Any non-success outcome (config gap, ok:false, non-2xx, or network error). */
export interface ShippingLabelFailure {
  ok: false
  error: string
}

export type ShippingLabelResult = ShippingLabelSuccess | ShippingLabelFailure

/** True when every Shippo-required field on the address is a non-blank string. */
export function isCompleteShippingAddress(address: UiAddressConfig | undefined): boolean {
  if (!address) return false
  return REQUIRED_ADDRESS_FIELDS.every((field) => {
    const value = address[field]
    return typeof value === 'string' && value.trim().length > 0
  })
}

export type ResolvedShippingAddresses =
  { ok: true; addressFrom: UiAddressConfig; addressTo: UiAddressConfig } | { ok: false; error: string }

/** Trim a value only when it is a non-empty string; otherwise return undefined. */
function cleanField(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : undefined
}

/**
 * Map ``config.orgAddress`` (typed :interface:`UiOrgAddressConfig`, which has no
 * name/phone/email) into the full :interface:`UiAddressConfig` shape the
 * ``addressFrom`` contract requires.
 *
 * ``name`` is sourced with precedence ``orgAddress.name`` (only if the runtime
 * payload carries one — the projected type does not) ?? ``config.clientName``,
 * so the contract-required sender name is populated from the best available org
 * display name. ``phone``/``email`` are preserved when the runtime address
 * carries them (never silently dropped). When no name is derivable the result
 * has no ``name`` field and :func:`isCompleteShippingAddress` rejects it.
 */
export function buildFromAddress(config: UiConfig | undefined): UiAddressConfig {
  // config is parsed from an untyped microapp payload, so orgAddress may carry
  // fields (name/phone/email) that UiOrgAddressConfig does not declare.
  const raw = (config?.orgAddress ?? {}) as Record<string, unknown>
  const out: UiAddressConfig = {}
  for (const key of ['street', 'street2', 'city', 'state', 'zip', 'country', 'phone', 'email'] as const) {
    const value = cleanField(raw[key])
    if (value !== undefined) out[key] = value
  }
  const name = cleanField(raw['name']) ?? cleanField(config?.clientName)
  if (name !== undefined) out.name = name
  return out
}

/**
 * Map ``config.orgAddress`` → ``addressFrom`` (via :func:`buildFromAddress`,
 * which populates the contract-required ``name``) and
 * ``config.wetLab.shipToAddress`` → ``addressTo``. Returns a typed config error
 * (rather than partial addresses) when either side is missing or incomplete —
 * including a missing ``name`` — so the caller never calls the label endpoint
 * with a contract-violating body.
 */
export function resolveShippingAddresses(config: UiConfig | undefined): ResolvedShippingAddresses {
  const addressFrom = buildFromAddress(config)
  const addressTo = getShipToAddress(config)

  if (!isCompleteShippingAddress(addressFrom)) {
    return {
      ok: false,
      error:
        'The organization address is missing or incomplete (a name, street, city, state, and ZIP are required). Add a complete contact address in client config before generating a shipping label.',
    }
  }
  if (!isCompleteShippingAddress(addressTo)) {
    return {
      ok: false,
      error:
        'The wet-lab ship-to address is missing or incomplete (a name, street, city, state, and ZIP are required). Add a complete WET_LAB ship-to address in client config before generating a shipping label.',
    }
  }
  return { ok: true, addressFrom, addressTo }
}

function toFailure(body: unknown, fallback: string): ShippingLabelFailure {
  if (body && typeof body === 'object' && typeof (body as { error?: unknown }).error === 'string') {
    return { ok: false, error: (body as { error: string }).error }
  }
  return { ok: false, error: fallback }
}

/**
 * POST the label request and normalize the response into a
 * :type:`ShippingLabelResult`. Honest by construction: a 2xx with ``ok:false``
 * or a missing ``labelUrl`` is reported as a failure; a rejected fetch becomes a
 * failure result instead of throwing. Skips the network call when either
 * address is incomplete.
 */
export async function requestShippingLabel(
  client: ProviderAPIClient,
  request: ShippingLabelRequest
): Promise<ShippingLabelResult> {
  if (!isCompleteShippingAddress(request.addressFrom) || !isCompleteShippingAddress(request.addressTo)) {
    return {
      ok: false,
      error: 'Shipping addresses are incomplete. A complete organization and wet-lab ship-to address are required.',
    }
  }

  let response: Response
  try {
    response = await client.post(ENDPOINT, request, JSON_HEADERS)
  } catch (error) {
    Sentry.captureException(error, { tags: { operation: 'kits.shipping-label' } })
    return { ok: false, error: "Couldn't create the shipping label. Try again." }
  }

  let body: unknown = null
  try {
    body = await response.json()
  } catch {
    body = null
  }

  if (!response.ok) {
    return toFailure(body, `Shipping-label request failed (${response.status}).`)
  }

  const parsed = body as Partial<ShippingLabelSuccess> & { ok?: boolean; error?: string }
  if (parsed?.ok !== true || typeof parsed.labelUrl !== 'string' || parsed.labelUrl.trim().length === 0) {
    return toFailure(body, 'The shipping label could not be generated.')
  }

  return {
    ok: true,
    labelUrl: parsed.labelUrl,
    trackingNumber: parsed.trackingNumber,
    trackingUrlProvider: parsed.trackingUrlProvider,
    carrier: parsed.carrier,
    labelFileType: parsed.labelFileType,
    transactionId: parsed.transactionId,
  }
}

/** Endpoint of the SSRF-guarded, JWT-authenticated Shippo label preview proxy. */
const PREVIEW_ENDPOINT = '/kits/shipping-label/preview'

/** Result of fetching the inline label preview PDF bytes. */
export type LabelPreviewResult = { ok: true; bytes: Uint8Array } | { ok: false; error: string }

/**
 * Fetch the Shippo label through the authenticated provider-api proxy and
 * return the raw PDF bytes so the caller can render them with the shared
 * ``PDFViewer`` (pdfjs canvas), mirroring the DRAFT-TRF streaming flow
 * (:func:`fetchDraftTrfPdfBytes`).
 *
 * Rendering the bytes to a canvas — rather than binding a ``blob:`` object URL
 * to an ``<iframe src>`` — is what makes the preview work inside the Epic SMART
 * embedded view: Epic hosts the SPA in an iframe, and a nested iframe pointing
 * at a ``blob:`` PDF is blocked by the browser/CSP, so it renders blank there.
 * Canvas rasterization has no such restriction. (It also sidesteps Shippo's
 * ``X-Frame-Options`` on the raw label URL, same as the previous blob path.)
 *
 * Honest by construction and never throws: a rejected fetch, a non-ok
 * response, a body read error, or an empty/zero-byte payload all map to
 * ``{ ok: false }`` so the caller shows the error state instead of a broken or
 * blank preview.
 */
export async function fetchLabelPreviewBytes(client: ProviderAPIClient, labelUrl: string): Promise<LabelPreviewResult> {
  let response: Response
  try {
    response = await client.get(`${PREVIEW_ENDPOINT}?url=${encodeURIComponent(labelUrl)}`)
  } catch (error) {
    Sentry.captureException(error, { tags: { operation: 'kits.shipping-label-preview' } })
    return { ok: false, error: 'The shipping label preview could not be loaded.' }
  }

  if (!response.ok) {
    return { ok: false, error: 'The shipping label preview could not be loaded.' }
  }

  let buffer: ArrayBuffer
  try {
    buffer = await response.arrayBuffer()
  } catch {
    return { ok: false, error: 'The shipping label preview could not be loaded.' }
  }

  if (!buffer || buffer.byteLength === 0) {
    return { ok: false, error: 'The shipping label preview could not be loaded.' }
  }

  return { ok: true, bytes: new Uint8Array(buffer) }
}
